import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { BACKEND_API_URL } from "../../config";

// Kept in sync with backend allowed_options (api/views.py -> handle_processing_plan)
export const PROCESSING_PLAN_OPTIONS = [
  'Branch',
  'Corporate',
  'Contract',
  'Hybrid',
  'Other',
];

const initialState = {
  data: {
    option: "",
    other: "",
  },
  status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
  loading: false,
  error: null,
};

export const fetchInitialState = createAsyncThunk(
  'ProcessingPlan/fetch',
  async (_, { getState, rejectWithValue }) => {
    const token = getState().navigator.token;

    try {
      const response = await fetch(`${BACKEND_API_URL}/api/onboarding/processing_plan?token=${token}`, {
        // A 404 means the user has not saved this optional step yet.
        errorToast: false,
      });

      // Not saved yet -> backend returns 404. Treat as empty, not an error.
      if (response.status === 404) {
        return { option: "", other: "" };
      }

      if (!response.ok) {
        console.error(`Failed to fetch initial state: ${response.status} ${response.statusText}`);
        return rejectWithValue('Failed to fetch initial state');
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('Error fetching initial state:', error);
      return rejectWithValue('An error occurred while fetching the initial state');
    }
  }
);

// Create New State / Update
export const SubmitProcessingPlan = createAsyncThunk(
  'ProcessingPlan/submit',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token, currentStep } = getState().navigator;

      const selectedOption = data?.option;
      if (!PROCESSING_PLAN_OPTIONS.includes(selectedOption)) {
        return rejectWithValue('Please select a processing plan.');
      }

      if (selectedOption === 'Other' && !(data?.other || '').trim()) {
        return rejectWithValue('Please specify your processing plan.');
      }

      // When not "Other", never send a stale specify value.
      const payloadData = {
        option: selectedOption,
        other: selectedOption === 'Other' ? data.other.trim() : '',
      };

      const response = await fetch(`${BACKEND_API_URL}/api/onboarding/processing_plan?token=${token}`, {
        method: 'POST',
        body: JSON.stringify({ data: payloadData, current_step: currentStep }),
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const response_data = await response.json();

      if (!response.ok) {
        return rejectWithValue(response_data?.error || 'Failed to save processing plan.');
      }

      return response_data.data;
    } catch (error) {
      console.error('Form submission error', error);
      return rejectWithValue(error.message);
    }
  }
);

export const processingPlanSlice = createSlice({
  name: 'ProcessingPlan',
  initialState,
  reducers: {
    setOption: (state, action) => {
      state.data.option = action.payload;
      // Clear the specify field when switching away from "Other"
      if (action.payload !== 'Other') {
        state.data.other = "";
      }
    },
    setOther: (state, action) => {
      state.data.other = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE or UPDATE
      .addCase(SubmitProcessingPlan.pending, (state) => {
        state.loading = true;
      })
      .addCase(SubmitProcessingPlan.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.data = {
            option: action.payload.option || "",
            other: action.payload.other || "",
          };
        }
      })
      .addCase(SubmitProcessingPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // GET
      .addCase(fetchInitialState.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInitialState.fulfilled, (state, action) => {
        state.loading = false;
        state.data = {
          option: action.payload?.option || "",
          other: action.payload?.other || "",
        };
      })
      .addCase(fetchInitialState.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setOption, setOther } = processingPlanSlice.actions;

export default processingPlanSlice.reducer;
